import { Fragment } from "react"
import { noChange, useRefresh } from "lib/@hooks/useRefresh"
import { useContentLanguage } from "routes/schedule/plugins/translation/content-language-context"
import { deriveMessageId } from "./derive-message-id"
import { interpretMessage } from "./dsl-interpreter"
import { getMessageDefinition } from "./message-definitions"
import { parseRichText } from "./parse-rich-text"
import { getProgram, MessageProgramsUpdated } from "./program-cache"

/**
 * Render a localized template using compiled programs when available, else English interpolation fallback.
 *
 * Rich text tags (`<b>`, `<i>`, `<u>`) in the translated output are auto-detected and
 * rendered as React elements (`<strong>`, `<em>`, `<u>`).  Pass `components` to add
 * custom tag mappings (e.g. `{ link: <a href="/details" /> }`).
 *
 * @param {object} props
 * @param {string} props.template
 * @param {Record<string, unknown>} [props.values]
 * @param {string} [props.id] - explicit message id override
 * @param {Record<string, { count: string }>} [props.vocabulary] - vocabPlural hints (merged with registered catalogue metadata)
 * @param {Record<string, string|import("react").ReactElement>} [props.components] - custom tag→component overrides for rich text
 */
export function LocalizedMessage({ template, values, id: explicitId, vocabulary, components }) {
    const { language } = useContentLanguage()
    const locale = language || "en"
    const msgId = deriveMessageId(template, explicitId)
    const program = getProgram(msgId)
    const refresh = useRefresh(noChange)
    MessageProgramsUpdated.useEvent(refresh, [])

    const def = getMessageDefinition(template, explicitId)
    const vocabPlural = { ...(def?.vocabPlural ?? {}), ...(vocabulary ?? {}) }
    const meta = { vocabPlural }

    const node = interpretMessage(program, template, values ?? {}, locale, meta)
    const rich = typeof node === "string" ? parseRichText(node, components) : node

    return <Fragment>{rich}</Fragment>
}
